import { z } from 'zod';

export const healthStatusSchema = z.object({
  status: z.enum(['ok', 'degraded']),
  service: z.string().min(1),
  timestamp: z.iso.datetime(),
  version: z.string().min(1),
});

export type HealthStatus = z.infer<typeof healthStatusSchema>;

export {
  COMMISSION_ITEM_KINDS,
  COMMISSION_PACKAGE_MODES,
  COMMISSION_RATE_TYPES,
  EMPLOYMENT_BONDS,
  commissionAccountCode,
  commissionAdvanceHelp,
  commissionItemKindLabels,
  commissionPackageModeLabels,
  commissionWarnings,
  employmentBondLabels,
  type CommissionItemKind,
  type CommissionPackageMode,
  type CommissionRate,
  type CommissionRateType,
  type CommissionWarning,
  type EmploymentBond,
} from './commissions.js';
export {
  coreModuleKeys,
  findFeature,
  findModule,
  moduleCatalog,
  sellableModules,
  validateCapabilitySelection,
  validateModuleCatalog,
  type CapabilityDependencies,
  type CapabilitySelection,
  type CapabilitySelectionValidation,
  type FeatureDefinition,
  type FeatureKey,
  type FeatureKeyOf,
  type MissingCapabilityRequirement,
  type ModuleDefinition,
  type ModuleKey,
} from './module-catalog.js';

export {
  ACCOUNTING_GROUPS,
  SUGGESTED_CHART,
  accountGroupOf,
  accountLevelOf,
  accountParentOf,
  isAccountingGroupCode,
  normalDirectionsOf,
  validateAccountCode,
  type AccountingDirection,
  type AccountingGroup,
  type AccountingGroupCode,
} from './chart-of-accounts.js';

export {
  findPermission,
  permissionCatalog,
  type PermissionDefinition,
  type PermissionKey,
} from './permission-catalog.js';
export {
  GUIDE_MARK_STATUSES,
  guideIsContracted,
  guideStepIsContracted,
  guideStepAcceptsConfirmation,
  guideStepTitle,
  guideCatalog,
  isGuideStep,
  type GuideKey,
  type GuideMarkStatus,
} from './guide-catalog.js';

export const problemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  requestId: z.string().optional(),
  errors: z.record(z.string(), z.array(z.string())).optional(),
});

export type ProblemDetails = z.infer<typeof problemDetailsSchema>;

export {
  canReachScreen,
  companyMenuGroupLabels,
  companyScreens,
  findCompanyScreen,
  moduleForCompanyScreen,
  resolveCompanyScreenModule,
  type CompanyMenuGroup,
  type CompanyScreen,
} from './screens.js';

export {
  RECONCILIATION_RULE_MODES,
  RECONCILIATION_RULE_OPERATORS,
  RECONCILIATION_RULE_TARGET_KINDS,
  RuleValidationError,
  assertAmountRange,
  assertSafePattern,
  assertTypedPattern,
  buildSimplePattern,
  compileRulePattern,
  familyKeyOf,
  normalizeForMatch,
  normalizeTerm,
  prepareRule,
  ruleHit,
  safeRegex,
  suggestTermFrom,
  trialOf,
  type PreparedRule,
  type ReconciliationRuleMode,
  type ReconciliationRuleOperator,
  type ReconciliationRuleTarget,
  type ReconciliationRuleTargetKind,
  type RuleCondition,
  type RuleHit,
  type RuleMatcher,
  type RuleSubject,
  type RuleTrialOutcome,
} from './reconciliation-rule.js';

export { DEFAULT_PHONE_COUNTRY, formatPhone, readPhone, type PhoneReading } from './phone.js';

export {
  BRAZILIAN_STATES,
  countryCodes,
  isBrazilianPostalCode,
  normalizeInstagram,
  type BrazilianState,
} from './client-contact.js';

export { digitsOf, isValidCnpj, isValidCpf } from './brazilian-document.js';

export { richTextToPlain, sanitizeRichText } from './rich-text.js';

export {
  HEALTH_THEME_COLORS,
  OCCURRENCE_SEVERITIES,
  OCCURRENCE_SEVERITY_LABELS,
  OCCURRENCE_TYPES,
  OCCURRENCE_TYPE_LABELS,
  RECORD_BLOCK_KINDS,
  RECORD_QUESTION_TYPES,
  RECORD_SIGNATURE_KINDS,
  RECORD_SIGNATURE_POLICIES,
  RECORD_TEMPLATE_KINDS,
  SIGNATURE_LIMITS,
  SYSTEM_HEALTH_THEMES,
  THEMED_QUESTION_TYPES,
  answerText,
  blockIssue,
  carryAnswers,
  compareRecordVersions,
  healthSignal,
  isSystemHealthTheme,
  questionsOf,
  reviewAnswers,
  reviewSignatureStrokes,
  reviewTemplateBlocks,
  signatureBounds,
  summarizeHealth,
  type HealthDeclaration,
  type HealthDenial,
  type HealthDivergence,
  type HealthRecordSource,
  type HealthTheme,
  type HealthThemeColor,
  type OccurrenceSeverity,
  type OccurrenceType,
  type RecordAnswer,
  type RecordAnswerChange,
  type RecordAnswers,
  type RecordBlock,
  type RecordBlockKind,
  type RecordIssue,
  type RecordOption,
  type RecordQuestion,
  type RecordQuestionType,
  type RecordSignatureKind,
  type RecordSignaturePolicy,
  type RecordTemplateKind,
  type RecordVersionContent,
  type SignatureStroke,
  type YesNoAnswer,
} from './records.js';

export {
  prepaidPolicySchema,
  type PrepaidPolicy,
  type PrepaidOverrides,
  type PrepaidRules,
  type PrepaidBalance,
  type PrepaidEvent,
  type PrepaidAction,
  type PrepaidSettings,
  type PrepaidStockOptions,
} from './prepaid-balances.js';

export {
  schedulingDepositPolicySchema,
  defaultSchedulingDepositPolicy,
  reviewSchedulingDepositPolicy,
  resolveSchedulingDepositPolicy,
  schedulingDepositRequirement,
  schedulingNoticeLabels,
  type SchedulingDepositPolicy,
  type SchedulingDepositSettings,
  type SchedulingNoticeKind,
  type SchedulingNotice,
} from './scheduling-policy.js';

export {
  applySchedulingCancellationPolicy,
  defaultSchedulingCancellationPolicy,
  describeSchedulingCancellationPolicy,
  resolveSchedulingCancellationPolicy,
  reviewSchedulingCancellationPolicy,
  schedulingCancellationPolicySchema,
  schedulingCancellationSnapshotSchema,
  type SchedulingCancellationOutcome,
  type SchedulingCancellationPolicy,
  type SchedulingCancellationSettings,
  type SchedulingCancellationSnapshot,
  type SchedulingCancellationSource,
  type SchedulingCancellationTier,
  type SchedulingNoShowRule,
  type SchedulingRescheduleRule,
  type SchedulingRetention,
} from './cancellation-policy.js';

export {
  LEGAL_DOCUMENTS,
  LEGAL_DOCUMENT_KEYS,
  PREFERRED_NAME_MAX_LENGTH,
  type LegalDocumentKey,
} from './legal.js';

export {
  socialLinksSchema,
  socialLinkSchema,
  socialLinkLabel,
  socialLinkUrl,
  socialNetworkLabels,
  type SocialLink,
} from './social-links.js';

export { portalPageSchema, defaultPortalPage, type PortalPageSettings } from './portal-page.js';

export {
  paymentInstructionsSchema,
  recurringPaymentInstructionsSchema,
  type PaymentInstructions,
} from './payment-instructions.js';

export {
  contactNumbersText,
  updateCallContact,
  contactNumbersSchema,
  publicContactNumbers,
  contactChannelLabels,
  contactNumberUrl,
  type ContactNumber,
  type ContactChannel,
} from './contact-numbers.js';

export { oneOrAll, repeatedQuery } from './query-choices.js';

export {
  reimbursementSchema,
  reimbursementMethodLabels,
  type Reimbursement,
} from './reimbursement.js';
